import { DialogComponent } from '@theme/dialog';

/**
 * "Let's talk" corner bubble that opens the contact form popup
 * (sections/cb-cta-bubble.liquid).
 *
 * - Shown (`data-visible`) once the page is scrolled `data-offset` percent of
 *   one screen height, and hidden again as soon as the footer starts to come
 *   into view; the CSS shrinks and fades it out.
 * - Scroll changes are ignored while the popup is open: the dialog locks the
 *   page by fixing the body, which resets window.scrollY to 0.
 * - In the theme editor the bubble shows without scrolling so it can be seen
 *   and clicked.
 *
 * @extends {DialogComponent}
 */
class CtaBubble extends DialogComponent {
  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {IntersectionObserver | undefined} */
  #footerObserver;

  #scrolled = false;
  #footerInView = false;

  connectedCallback() {
    super.connectedCallback();

    this.#abortController?.abort();
    this.#abortController = new AbortController();
    window.addEventListener('scroll', this.#handleScroll, { passive: true, signal: this.#abortController.signal });

    this.#footerObserver?.disconnect();
    const footer = document.querySelector('body > footer') ?? document.querySelector('footer');
    if (footer) {
      this.#footerObserver = new IntersectionObserver(([entry]) => {
        this.#footerInView = Boolean(entry?.isIntersecting);
        this.#update();
      });
      this.#footerObserver.observe(footer);
    }

    this.#handleScroll();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController?.abort();
    this.#footerObserver?.disconnect();
  }

  #handleScroll = () => {
    if (this.refs.dialog?.open) return;

    const offset = Math.max(0, Number(this.dataset.offset) || 0) / 100;
    this.#scrolled = Boolean(window.Shopify?.designMode) || window.scrollY >= window.innerHeight * offset;
    this.#update();
  };

  #update() {
    this.toggleAttribute('data-visible', this.#scrolled && !this.#footerInView);
  }
}

if (!customElements.get('cb-cta-bubble')) {
  customElements.define('cb-cta-bubble', CtaBubble);
}
